<script setup lang="ts">
import { computed, ref } from 'vue';
import { useOverlaySize } from '@/composables/isDesktop';
import { X } from 'lucide-vue-next';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import type { Option } from '@/stores/store-interfaces';
import { cn } from '@/lib/utils';
import IconSmallDownArrow from '@/components/icons/IconSmallDownArrow.vue';
import { Check, Plus } from 'lucide-vue-next';

const props = withDefaults(
    defineProps<{
        options: Option[];
        modelValue: Option[];
        placeholder?: string;
        label?: string;
        descriptionId?: string;
        disabled?: boolean;
        emptyText?: string;
        errorMessage?: string;
        required?: boolean;
        multiSelect?: boolean;
        hideSelectedBadges?: boolean;
        /**
         * Title for the mobile sheet header (ITDEVELOP-3147). Callers that
         * render their own label outside this component (form-renderer) pass
         * the field label here so the sheet does not fall back to a generic
         * word that reads like a button.
         */
        sheetTitle?: string;
        /**
         * Extra classes for the trigger button, merged after the defaults, so a
         * caller that sizes the field by its column can drop the 180px floor
         * (`min-w-0`) or set a width without a wrapper.
         */
        triggerClass?: string;
        /** Accessible name of the trigger when no visible label is rendered; falls back to `label`. */
        ariaLabel?: string;
        /**
         * Whether the desktop popover offers the filter box (and, with it, the clear button).
         * A search field over a handful of options is noise, the same judgement the mobile
         * sheet already makes by option count; callers with short lists turn it off.
         */
        filterable?: boolean;
        /**
         * Optional action row pinned as the first item of the list (e.g.
         * "Create new visitor"). Selecting it emits `action` instead of
         * changing the value, and closes the popover / sheet so the caller
         * can open its own dialog.
         */
        actionLabel?: string;
    }>(),
    {
        placeholder: 'Select options…',
        label: undefined,
        descriptionId: undefined,
        disabled: false,
        emptyText: 'Nothing found.',
        hideSelectedBadges: false,
        sheetTitle: undefined,
        triggerClass: undefined,
        ariaLabel: undefined,
        filterable: true,
        actionLabel: undefined,
    },
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: Option[]): void;
    (e: 'change', value: Option[]): void;
    (e: 'action'): void;
}>();

const open = ref(false);

// ITDEVELOP-3147: on a PHONE (<768 — tablets keep the desktop popover, per
// the breakpoint ruling on the ticket) a select opens a sheet instead of a
// popover: full screen with a filter box past the option-count threshold,
// compact otherwise. Multi-select is STAGED on mobile: the sheet edits a
// draft and only Apply commits, so Cancel / scrim / Escape genuinely discard
// — the explicit Apply/Cancel semantic the overlay decision requires.
// Single-select keeps tap-to-commit-and-close, exactly like the popover.
const overlaySize = useOverlaySize();

// The rule is keyed on option count: 15+ (or searchable) earns the full-screen
// treatment with the filter box; anything smaller gets a compact content-height
// sheet with NO filter input — a search field over four options is noise, and
// the keyboard it summons is the thing the full-screen container exists to
// absorb. (The rule's 2–5 → no-overlay tier is a form-renderer decision —
// render radios/chips instead of a select — not something this primitive can
// apply to itself.)
const FULL_SCREEN_THRESHOLD = 15;
const fullScreen = computed(() => props.options.length > FULL_SCREEN_THRESHOLD);

const mobileTitle = computed(
    () => props.sheetTitle || props.label || (props.multiSelect ? 'Select options' : 'Select an option'),
);

const draft = ref<Option[]>([]);
const draftCount = computed(() => draft.value.length);

function openSheet() {
    if (props.disabled) return;
    draft.value = [...props.modelValue];
    open.value = true;
}

function isDrafted(option: Option) {
    return draft.value.some((o) => o.value === option.value);
}

function toggleMobile(option: Option) {
    if (option.disabled) return;
    if (!props.multiSelect) {
        // single-select: commit and close, as the popover does
        toggle(option);
        return;
    }
    draft.value = isDrafted(option) ? draft.value.filter((o) => o.value !== option.value) : [...draft.value, option];
}

function applyDraft() {
    emit('update:modelValue', [...draft.value]);
    emit('change', [...draft.value]);
    open.value = false;
}

function clearDraft() {
    draft.value = [];
}

function cancelSheet() {
    // The draft is reseeded on every open, so closing without Apply discards.
    open.value = false;
}

const count = computed(() => props.modelValue.length);
const hasSelection = computed(() => count.value > 0);
const selectedLabels = computed(() => props.modelValue.map((o) => o.label));

function isSelected(option: Option) {
    return props.modelValue.some((o) => o.value === option.value);
}
const showPlaceholder = computed(() => {
    if (props.multiSelect) {
        return count.value === 0;
    } else {
        return props.modelValue.length == 0;
    }
});

const buttonLabel = computed(() => {
    if (showPlaceholder.value) {
        return props.placeholder;
    }

    if (props.multiSelect) {
        return `${count.value} selected`;
    } else {
        return props.modelValue[0]?.label;
    }
});

function toggle(option: Option) {
    if (option.disabled) return;
    if (props.multiSelect) {
        // multi-select
        const exists = isSelected(option);
        const next = exists ? props.modelValue.filter((o) => o.value !== option.value) : [...props.modelValue, option];
        emit('update:modelValue', next);
        emit('change', next);
    } else {
        // single-select
        // If selecting the same value, just close
        if (isSelected(option)) {
            open.value = false;
            return;
        }
        const nextValue = [option];
        emit('update:modelValue', nextValue);
        emit('change', nextValue);
        open.value = false;
    }
}

function clearAll() {
    emit('update:modelValue', []);
    emit('change', []);
}

function runAction() {
    open.value = false;
    emit('action');
}
</script>

<template>
    <div class="gap-xs flex w-full flex-col">
        <div v-if="label || required" class="gap-xs flex">
            <span v-if="required" class="text-error">*</span>
            <label>{{ label }}</label>
        </div>
        <Popover v-if="overlaySize === 'desktop'" v-model:open="open">
            <!--
                tabindex="0" on the trigger is deliberate: macOS Safari leaves plain
                <button> out of its default Tab cycle (Keyboard navigation off = text
                fields and pop-up lists only), so Tab skipped this combobox entirely
                and focus left the document. An explicit tabindex puts it back into
                sequential focus navigation.
            -->
            <PopoverTrigger as-child :disabled="disabled">
                <Button
                    :disabled="disabled"
                    :variant="props.errorMessage ? 'outline-error' : 'outline'"
                    size="field"
                    class="bg-lightest flex w-full items-center justify-between rounded-sm border border-(--border-subtle) text-base"
                    :class="
                        cn(
                            'min-w-45', // temporary workaround to align with input width
                            { 'input-error': props.errorMessage },
                            props.triggerClass,
                        )
                    "
                    role="combobox"
                    :aria-expanded="open"
                    :aria-label="ariaLabel || label || 'Select options'"
                    :aria-describedby="descriptionId"
                    tabindex="0"
                >
                    <span
                        class="truncate"
                        :class="{
                            // --text-muted, not --text-placeholder or a raw gray:
                            // this is a real <span>, so unlike a native input
                            // placeholder it is held to the WCAG AA text minimum
                            // (gray-300 #848484 measured 3.74:1 light / 4.39:1
                            // dark — ITDEVELOP-2736), and ui/* colours must come
                            // from semantic tokens (ADR-0035).
                            'text-(--text-muted)': showPlaceholder,
                        }"
                        :title="buttonLabel"
                        >{{ buttonLabel }}</span
                    >
                    <IconSmallDownArrow />
                </Button>
            </PopoverTrigger>

            <PopoverContent class="ignore-click-outside w-80 p-0" align="start">
                <Command>
                    <CommandInput v-if="filterable" :placeholder="'Filter options…'">
                        <template #trailing>
                            <button
                                v-if="hasSelection"
                                type="button"
                                class="text-dark-400 inline-flex items-center justify-center rounded-sm p-1 hover:bg-blue-50 hover:text-blue-500"
                                aria-label="Clear selection"
                                @click="clearAll()"
                            >
                                <X class="h-4 w-4" />
                            </button>
                        </template>
                    </CommandInput>
                    <!-- Pinned above the filtered list so it survives a non-matching search. -->
                    <button v-if="actionLabel" type="button" class="select-action" @click="runAction">
                        <Plus aria-hidden="true" />
                        <span>{{ actionLabel }}</span>
                    </button>
                    <CommandList>
                        <CommandEmpty>{{ emptyText }}</CommandEmpty>

                        <CommandGroup>
                            <CommandItem
                                v-for="opt in options"
                                :key="opt.value"
                                :value="opt.label"
                                :disabled="opt.disabled"
                                @select="() => toggle(opt)"
                            >
                                <Check
                                    v-if="!multiSelect"
                                    class="mr-2 h-4 w-4 transition-opacity"
                                    :class="isSelected(opt) ? 'opacity-100' : 'opacity-0'"
                                />
                                <input
                                    v-if="multiSelect"
                                    type="checkbox"
                                    :checked="isSelected(opt)"
                                    class="pointer-events-none"
                                />

                                <span :class="opt.disabled ? 'opacity-50' : ''">{{ opt.label }}</span>
                            </CommandItem>
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>

        <!-- Mobile (ITDEVELOP-3147): same trigger, full-screen container. The
             trigger markup mirrors the popover trigger above — if you change
             one, change both. -->
        <template v-else>
            <Button
                :disabled="disabled"
                type="button"
                tabindex="0"
                :variant="props.errorMessage ? 'outline-error' : 'outline'"
                size="field"
                class="bg-lightest flex w-full items-center justify-between rounded-sm border border-(--border-subtle) text-base"
                :class="
                    cn(
                        'min-w-45', // temporary workaround to align with input width
                        { 'input-error': props.errorMessage },
                        props.triggerClass,
                    )
                "
                role="combobox"
                :aria-expanded="open"
                :aria-label="ariaLabel || label || 'Select options'"
                :aria-describedby="descriptionId"
                @click="openSheet"
            >
                <span class="truncate" :class="{ 'text-(--text-muted)': showPlaceholder }" :title="buttonLabel">{{
                    buttonLabel
                }}</span>
                <IconSmallDownArrow />
            </Button>

            <Sheet v-model:open="open">
                <SheetContent
                    side="bottom"
                    class="z-70 flex flex-col gap-0 p-0"
                    :class="fullScreen ? 'h-dvh' : 'max-h-[70dvh] rounded-t-lg'"
                    hide-close
                >
                    <!-- Compact single-select is chrome-free (QA: a Cancel
                         button and a title duplicate what tap-to-pick, the
                         visible scrim and Escape already provide), so the title
                         goes screen-reader only. No grabber either — ui/sheet
                         has no swipe-to-dismiss, and a handle that does not
                         drag reads as broken (QA 2026-08-21). Multi-select
                         needs its staged Apply/Cancel, and FULL SCREEN always
                         keeps the header — it covers the scrim, so an explicit
                         exit must stay visible. -->
                    <template v-if="!fullScreen && !multiSelect">
                        <SheetTitle class="sr-only">{{ mobileTitle }}</SheetTitle>
                    </template>
                    <!-- Title truly centered (relative + absolute side actions,
                         same pattern as the filter sheets — the grow-centered
                         title sat off optical center, MR !312 review). The
                         header follows the convention: multi-select carries
                         actions (Cancel + a filled primary Apply — it is the
                         commit action, not a link); single-select carries none
                         (tap-to-commit), so it gets the centered title + X. -->
                    <div
                        v-else
                        class="p-sm relative flex min-h-12 items-center justify-center border-b border-(--border-subtle)"
                    >
                        <button v-if="multiSelect" type="button" class="btn-link left-sm absolute" @click="cancelSheet">
                            Cancel
                        </button>
                        <!-- text-base, not the filter sheets' text-lg: this
                             header carries actions on both sides, and field
                             labels truncate at the larger size. -->
                        <SheetTitle class="max-w-[55%] truncate p-0 text-center text-base font-bold">{{
                            mobileTitle
                        }}</SheetTitle>
                        <Button v-if="multiSelect" size="sm" class="right-sm absolute" @click="applyDraft">
                            Apply
                        </Button>
                        <button v-else type="button" aria-label="Close" class="right-sm absolute" @click="cancelSheet">
                            <i class="i-exit block size-6" />
                        </button>
                    </div>

                    <Command class="flex min-h-0 flex-1 flex-col">
                        <CommandInput v-if="fullScreen && filterable" :placeholder="'Filter options…'">
                            <template #trailing>
                                <button
                                    v-if="multiSelect ? draftCount > 0 : hasSelection"
                                    type="button"
                                    class="text-dark-400 inline-flex items-center justify-center rounded-sm p-1 hover:bg-blue-50 hover:text-blue-500"
                                    aria-label="Clear selection"
                                    @click="multiSelect ? clearDraft() : clearAll()"
                                >
                                    <X class="h-4 w-4" />
                                </button>
                            </template>
                        </CommandInput>
                        <!-- Pinned above the filtered list so it survives a non-matching search. -->
                        <button v-if="actionLabel" type="button" class="select-action" @click="runAction">
                            <Plus aria-hidden="true" />
                            <span>{{ actionLabel }}</span>
                        </button>
                        <CommandList class="max-h-none flex-1">
                            <CommandEmpty>{{ emptyText }}</CommandEmpty>

                            <CommandGroup>
                                <!-- Reka's ListboxRoot highlights (and focuses)
                                     the selected/first item on mount, ungated in
                                     2.5.1 — on touch that paints a phantom
                                     selection. The compact tier has no keyboard,
                                     so the cue is suppressed; full screen keeps
                                     it for arrow-key nav from the filter box. -->
                                <CommandItem
                                    v-for="opt in options"
                                    :key="opt.value"
                                    :value="opt.label"
                                    :disabled="opt.disabled"
                                    :class="fullScreen ? '' : 'data-[highlighted]:bg-transparent'"
                                    @select="() => toggleMobile(opt)"
                                >
                                    <Check
                                        v-if="!multiSelect"
                                        class="mr-2 h-4 w-4 transition-opacity"
                                        :class="isSelected(opt) ? 'opacity-100' : 'opacity-0'"
                                    />
                                    <input
                                        v-if="multiSelect"
                                        type="checkbox"
                                        :checked="isDrafted(opt)"
                                        class="pointer-events-none"
                                    />

                                    <span :class="opt.disabled ? 'opacity-50' : ''">{{ opt.label }}</span>
                                </CommandItem>
                            </CommandGroup>
                        </CommandList>
                    </Command>

                    <div v-if="multiSelect" class="p-sm border-t border-(--border-subtle) text-sm text-(--text-muted)">
                        {{ draftCount }} selected
                    </div>
                </SheetContent>
            </Sheet>
        </template>

        <div v-if="selectedLabels.length && multiSelect && !hideSelectedBadges" class="flex flex-wrap gap-2">
            <Badge
                v-for="opt in modelValue"
                :key="opt.value"
                variant="secondary"
                class="cursor-pointer px-2 py-1"
                @click.stop="() => toggle(opt)"
            >
                {{ opt.label }}
                <X class="ml-1 h-3 w-3 opacity-60" />
            </Badge>
        </div>

        <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
    </div>
</template>

<style scoped>
@reference '@/assets/styles/default.css';
/* Mirrors CommandItem geometry with tighter vertical padding (py-xs vs py-sm). */
.select-action {
    @apply px-xs py-xs mx-1 mt-1 flex w-[calc(100%-0.5rem)] cursor-pointer items-center gap-2 rounded-sm text-base font-semibold hover:bg-(--surface-highlight) focus-visible:bg-(--surface-highlight) focus-visible:outline-none [&_svg]:size-4 [&_svg]:shrink-0;
}
</style>
